import { useEffect, useMemo } from 'react';

import { ScheduleCast, ScheduleUser } from '@components/index';
import { DATE_DAYJS_FORMAT } from '@enums/dates';
import { ISchedule } from '@interfaces/scheduleInterface';
import { Role } from '@interfaces/userInterface';
import { Tag } from 'antd';
import classNames from 'classnames';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useParams } from 'react-router-dom';

dayjs.extend(utc);
dayjs.extend(timezone);

interface IScheduleItemProps {
	schedule: ISchedule;
	setScheduleOpen: (scheduleId: number) => void;
	scheduleOpen: number;
}

const ScheduleItem = ({ schedule, setScheduleOpen, scheduleOpen }: IScheduleItemProps) => {
	const { scheduleId: scheduleIdFocus } = useParams();

	const showInformationHandler = (scheduleId: number | undefined) => {
		setScheduleOpen(scheduleId as number);
	};

	const has3d = useMemo(() => {
		return schedule.type.includes('3D');
	}, [schedule.type]);

	useEffect(() => {
		if (scheduleIdFocus) {
			const ref = document.getElementById(`schedule-${scheduleIdFocus}`);
			window.scrollTo({
				top: Number(ref?.offsetTop) - 42,
				behavior: 'smooth'
			});
		}
	}, [scheduleIdFocus]);

	return (
		<div
			className={classNames('mb-3 rounded-xl bg-white px-4 py-4 shadow', {
				'border-2 border-blue-400': Number(scheduleIdFocus) === schedule.id
			})}
			id={`schedule-${schedule.id}`}
		>
			<div className="flex flex-col gap-2 md:flex-row">
				<div className="md:w-col-30 flex flex-col items-center md:items-start">
					<div className="text-center text-lg font-bold md:text-left">{schedule.movie.title}</div>
					<div className="font-semibold">
						{dayjs(schedule.date).tz('Europe/Paris').format(DATE_DAYJS_FORMAT.FR_LONG)}{' '}
					</div>
					<div>{dayjs(schedule.date).tz('Europe/Paris').format('HH:mm')}</div>

					<div className="flex flex-wrap gap-2">
						{schedule.type.map((type) => (
							<div className="" key={type}>
								<Tag className="m-0 !ms-0 !me-0 p-0" color="#94A3B8">
									{type}
								</Tag>
							</div>
						))}
					</div>
				</div>
				<div className="flex w-full flex-col gap-4 text-center md:flex-row">
					<div className="md:w-col-20 w-full">
						<div className="font-bold">Responsable</div>
						<ScheduleUser schedule={schedule} user={schedule.responsable} role={Role.Responsable} />
					</div>
					<div className="md:w-col-20 w-full">
						<div className="font-bold">Caissier</div>
						<ScheduleUser schedule={schedule} user={schedule.cashier} role={Role.Cashier} />
					</div>
					<div className="md:w-col-20 w-full">
						<div className="font-bold">Opérateur</div>
						<ScheduleUser schedule={schedule} user={schedule.operator} role={Role.Operator} />
					</div>
					<div className="md:w-col-20 w-full">
						<div className="font-bold">Contrôleur</div>
						<ScheduleUser schedule={schedule} user={schedule.controller} role={Role.Controller} />
					</div>
					<div className="md:w-col-20 w-full">
						<div className="font-bold">Confiserie</div>
						<ScheduleUser schedule={schedule} user={schedule.store} role={Role.Store} />
					</div>
					{has3d && (
						<div className="md:w-col-20 w-full">
							<div className="font-bold">Lunettes 3D</div>
							<ScheduleUser schedule={schedule} user={schedule.glasses} role={Role.Glasses} />
						</div>
					)}
				</div>
			</div>

			<ScheduleCast
				schedule={schedule}
				open={schedule.id === scheduleOpen}
				setScheduleOpen={(scheduleId) => showInformationHandler(scheduleId)}
			/>
		</div>
	);
};

export default ScheduleItem;
